/**
 * Studio Intelligence Service
 * Advanced analytics API for Studio Intelligence Hub
 * Solis Games - Phase 2.2 Implementation
 */

import { supabase } from './supabase';

export const studioIntelligenceService = {
  /**
   * ============================================
   * COHORT RETENTION ANALYSIS
   * ============================================
   */

  /**
   * Get cohort retention data with heat map formatting
   * @param {string} studioId - Studio UUID
   * @param {string|null} gameId - Optional game UUID filter
   * @param {number} weeksBack - Number of weeks to look back (default 12)
   * @returns {Promise<{cohorts: Array, heatMapData: Array, summary: Object}>}
   */
  async getCohortRetention(studioId, gameId = null, weeksBack = 12) {
    try {
      const { data, error } = await supabase.rpc('get_studio_cohort_retention', {
        studio_id_param: studioId,
        game_id_param: gameId,
        weeks_back: weeksBack
      });

      if (error) throw error;

      if (!data || data.length === 0) {
        return {
          cohorts: [],
          heatMapData: [],
          summary: {
            avgDay1: '0.0',
            avgDay7: '0.0',
            avgDay30: '0.0',
            avgDay90: '0.0',
            trend: 'stable'
          }
        };
      }

      return {
        cohorts: data,
        heatMapData: this.transformToHeatMap(data),
        summary: this.calculateRetentionSummary(data)
      };
    } catch (error) {
      console.error('Error fetching cohort retention:', error);
      throw error;
    }
  },

  /**
   * Transform cohort data to heat map format
   * @param {Array} cohortData - Raw cohort data from database
   * @returns {Array} Heat map formatted data
   */
  transformToHeatMap(cohortData) {
    return cohortData.map(cohort => ({
      cohort: new Date(cohort.cohort_week).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      cohortSize: cohort.cohort_size,
      day1: parseFloat(cohort.day1_retention_pct) || 0,
      day7: parseFloat(cohort.day7_retention_pct) || 0,
      day30: parseFloat(cohort.day30_retention_pct) || 0,
      day90: parseFloat(cohort.day90_retention_pct) || 0
    }));
  },

  /**
   * Calculate retention summary statistics
   * @param {Array} cohortData - Raw cohort data
   * @returns {Object} Summary statistics
   */
  calculateRetentionSummary(cohortData) {
    if (!cohortData || cohortData.length === 0) {
      return {
        avgDay1: '0.0',
        avgDay7: '0.0',
        avgDay30: '0.0',
        avgDay90: '0.0',
        trend: 'stable'
      };
    }

    // Calculate averages from recent cohorts (last 4 weeks)
    const recentCohorts = cohortData.slice(0, Math.min(4, cohortData.length));

    const avgDay1 = recentCohorts.reduce((sum, c) => sum + parseFloat(c.day1_retention_pct || 0), 0) / recentCohorts.length;
    const avgDay7 = recentCohorts.reduce((sum, c) => sum + parseFloat(c.day7_retention_pct || 0), 0) / recentCohorts.length;
    const avgDay30 = recentCohorts.reduce((sum, c) => sum + parseFloat(c.day30_retention_pct || 0), 0) / recentCohorts.length;
    const avgDay90 = recentCohorts.reduce((sum, c) => sum + parseFloat(c.day90_retention_pct || 0), 0) / recentCohorts.length;

    // Calculate trend (comparing first 2 vs last 2 cohorts)
    const trend = this.calculateTrend(cohortData);

    return {
      avgDay1: avgDay1.toFixed(1),
      avgDay7: avgDay7.toFixed(1),
      avgDay30: avgDay30.toFixed(1),
      avgDay90: avgDay90.toFixed(1),
      trend
    };
  },

  /**
   * Calculate retention trend
   * @param {Array} cohortData - Raw cohort data
   * @returns {string} Trend direction ('improving', 'declining', 'stable')
   */
  calculateTrend(cohortData) {
    if (cohortData.length < 4) return 'stable';

    const recent = cohortData.slice(0, 2);
    const older = cohortData.slice(2, 4);

    const recentAvg = recent.reduce((sum, c) => sum + parseFloat(c.day7_retention_pct || 0), 0) / recent.length;
    const olderAvg = older.reduce((sum, c) => sum + parseFloat(c.day7_retention_pct || 0), 0) / older.length;

    const change = recentAvg - olderAvg;

    if (change > 5) return 'improving';
    if (change < -5) return 'declining';
    return 'stable';
  },

  /**
   * ============================================
   * COMPETITIVE BENCHMARKING
   * ============================================
   */

  /**
   * Get competitive benchmarks for a game
   * @param {string} gameId - Game UUID
   * @returns {Promise<Object>} Benchmark data with insights
   */
  async getCompetitiveBenchmarks(gameId) {
    try {
      const { data, error } = await supabase.rpc('get_game_competitive_benchmarks', {
        game_id_param: gameId
      });

      if (error) throw error;

      if (data.error) {
        return {
          error: data.error,
          yourGame: null,
          genreAverage: null,
          platformAverage: null,
          percentileRank: 0,
          insights: []
        };
      }

      return {
        yourGame: data.game_metrics,
        genreAverage: data.genre_averages,
        platformAverage: data.platform_averages,
        percentileRank: Math.round(data.percentile_rank),
        insights: this.generateBenchmarkInsights(data)
      };
    } catch (error) {
      console.error('Error fetching competitive benchmarks:', error);
      throw error;
    }
  },

  /**
   * Generate actionable insights from benchmark data
   * @param {Object} data - Benchmark data
   * @returns {Array} Array of insight objects
   */
  generateBenchmarkInsights(data) {
    const insights = [];
    const game = data.game_metrics;
    const genre = data.genre_averages;

    if (!game || !genre) return insights;

    // Engagement comparison
    if (game.engagement_score > genre.avg_engagement * 1.2) {
      const pctDiff = ((game.engagement_score / genre.avg_engagement - 1) * 100).toFixed(0);
      insights.push({
        type: 'success',
        title: 'Above Average Engagement',
        message: `Your game's engagement score is ${pctDiff}% higher than genre average. Players are highly engaged!`,
        actionable: false
      });
    } else if (game.engagement_score < genre.avg_engagement * 0.8) {
      const pctDiff = ((1 - game.engagement_score / genre.avg_engagement) * 100).toFixed(0);
      insights.push({
        type: 'warning',
        title: 'Below Average Engagement',
        message: `Your engagement is ${pctDiff}% below genre average. Consider improving onboarding or gameplay pacing.`,
        actionable: true
      });
    }

    // Session duration comparison
    if (game.avg_session_minutes < genre.avg_session * 0.7) {
      const diffMin = (genre.avg_session - game.avg_session_minutes).toFixed(1);
      insights.push({
        type: 'warning',
        title: 'Short Session Duration',
        message: `Players leave ${diffMin} minutes sooner than competitors. This could indicate onboarding issues or unclear objectives.`,
        actionable: true
      });
    } else if (game.avg_session_minutes > genre.avg_session * 1.3) {
      insights.push({
        type: 'success',
        title: 'Strong Session Length',
        message: 'Players spend significantly more time in your game than competitors. Great job keeping them engaged!',
        actionable: false
      });
    }

    // Percentile ranking
    if (data.percentile_rank >= 75) {
      insights.push({
        type: 'achievement',
        title: 'Top Performer',
        message: `You're in the top ${100 - Math.round(data.percentile_rank)}% of ${game.primary_genre} games on the platform!`,
        actionable: false
      });
    } else if (data.percentile_rank < 25) {
      insights.push({
        type: 'warning',
        title: 'Below Average Performance',
        message: `Your game ranks in the bottom quartile. Focus on the insights above to improve player retention and engagement.`,
        actionable: true
      });
    }

    // Player count comparison
    if (game.total_unique_players > genre.avg_players * 1.5) {
      insights.push({
        type: 'success',
        title: 'Strong Player Base',
        message: 'You have significantly more players than the genre average. Your marketing is working!',
        actionable: false
      });
    }

    // Quality score insight
    if (game.avg_quality_score && game.avg_quality_score < 50) {
      insights.push({
        type: 'warning',
        title: 'Low Session Quality',
        message: 'Players aren\'t triggering many events or achievements. Consider adding more interactive elements or clearer objectives.',
        actionable: true
      });
    }

    return insights;
  },

  /**
   * ============================================
   * PLAYER JOURNEY MAPPING
   * ============================================
   */

  /**
   * Get player journey funnel data
   * @param {string} gameId - Game UUID
   * @param {number} daysBack - Number of days to look back (default 30)
   * @returns {Promise<Object>} Journey funnel data with analysis
   */
  async getPlayerJourney(gameId, daysBack = 30) {
    try {
      const { data, error } = await supabase.rpc('get_player_journey_funnel', {
        game_id_param: gameId,
        days_back: daysBack
      });

      if (error) throw error;

      const funnelData = data || [];

      return {
        funnel: funnelData,
        dropOffAnalysis: this.analyzeDropOffs(funnelData),
        sourceBreakdown: this.getSourceBreakdown(funnelData)
      };
    } catch (error) {
      console.error('Error fetching player journey:', error);
      throw error;
    }
  },

  /**
   * Analyze drop-off points in the funnel
   * @param {Array} funnelData - Funnel stage data
   * @returns {Array} Drop-off analysis
   */
  analyzeDropOffs(funnelData) {
    const dropOffs = [];

    if (!funnelData || funnelData.length < 2) return dropOffs;

    for (let i = 1; i < funnelData.length; i++) {
      const prev = funnelData[i - 1];
      const curr = funnelData[i];

      if (!prev.users || prev.users === 0) continue;

      const dropRate = 100 - (curr.conversion_rate || 0);
      const usersLost = prev.users - curr.users;

      if (dropRate > 50) {
        dropOffs.push({
          stage: `${prev.stage} → ${curr.stage}`,
          dropRate: dropRate.toFixed(1),
          usersLost: usersLost,
          severity: dropRate > 70 ? 'critical' : 'high',
          recommendation: this.getDropOffRecommendation(prev.stage, curr.stage, dropRate)
        });
      }
    }

    return dropOffs;
  },

  /**
   * Get recommendation for drop-off point
   * @param {string} fromStage - Starting stage
   * @param {string} toStage - Ending stage
   * @param {number} dropRate - Drop-off rate
   * @returns {string} Recommendation text
   */
  getDropOffRecommendation(fromStage, toStage, dropRate) {
    if (fromStage === 'discovered' && toStage === 'interested') {
      return 'Players aren\'t clicking play. Improve your game description, screenshots, or add a trailer.';
    }
    if (fromStage === 'interested' && toStage === 'first_play') {
      return 'Loading or technical issues may be preventing plays. Check error logs and optimize load times.';
    }
    if (fromStage === 'first_play' && toStage === 'return_player') {
      return `${dropRate.toFixed(0)}% aren't returning. Improve first-time experience, add a tutorial, or clearer objectives.`;
    }
    if (fromStage === 'return_player' && toStage === 'engaged') {
      return 'Players return once but don\'t stick. Add progression systems, challenges, or social features.';
    }
    if (fromStage === 'engaged' && toStage === 'champion') {
      return 'Engaged players aren\'t converting to champions. Consider loyalty rewards or exclusive content.';
    }
    return 'Focus on improving this transition point.';
  },

  /**
   * Get breakdown of discovery sources
   * @param {Array} funnelData - Funnel stage data
   * @returns {Object} Source breakdown by stage
   */
  getSourceBreakdown(funnelData) {
    if (!funnelData || funnelData.length === 0) return {};

    const breakdown = {};

    funnelData.forEach(stage => {
      if (stage.sources && typeof stage.sources === 'object') {
        breakdown[stage.stage] = stage.sources;
      }
    });

    return breakdown;
  },

  /**
   * ============================================
   * SESSION QUALITY ANALYSIS
   * ============================================
   */

  /**
   * Get session quality distribution
   * @param {string} studioId - Studio UUID
   * @param {string|null} gameId - Optional game UUID filter
   * @param {number} daysBack - Number of days to look back
   * @returns {Promise<Object>} Quality distribution data
   */
  async getSessionQualityDistribution(studioId, gameId = null, daysBack = 30) {
    try {
      let query = supabase
        .from('user_game_sessions')
        .select(`
          quality_score,
          game_id,
          games!inner(studio_id)
        `)
        .eq('games.studio_id', studioId)
        .gte('session_start', new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000).toISOString())
        .not('quality_score', 'is', null);

      if (gameId) {
        query = query.eq('game_id', gameId);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (!data || data.length === 0) {
        return {
          distribution: [
            { quality: 'Poor (0-25)', count: 0, percentage: '0.0' },
            { quality: 'Fair (26-50)', count: 0, percentage: '0.0' },
            { quality: 'Good (51-75)', count: 0, percentage: '0.0' },
            { quality: 'Excellent (76-100)', count: 0, percentage: '0.0' }
          ],
          averageQuality: '0.0',
          totalSessions: 0
        };
      }

      // Group into quality buckets
      const buckets = {
        'Poor (0-25)': 0,
        'Fair (26-50)': 0,
        'Good (51-75)': 0,
        'Excellent (76-100)': 0
      };

      data.forEach(session => {
        const score = session.quality_score || 0;
        if (score <= 25) buckets['Poor (0-25)']++;
        else if (score <= 50) buckets['Fair (26-50)']++;
        else if (score <= 75) buckets['Good (51-75)']++;
        else buckets['Excellent (76-100)']++;
      });

      const total = data.length;
      const avgQuality = data.reduce((sum, s) => sum + (s.quality_score || 0), 0) / total;

      return {
        distribution: Object.entries(buckets).map(([quality, count]) => ({
          quality,
          count,
          percentage: ((count / total) * 100).toFixed(1)
        })),
        averageQuality: avgQuality.toFixed(1),
        totalSessions: total
      };
    } catch (error) {
      console.error('Error fetching session quality:', error);
      throw error;
    }
  },

  /**
   * ============================================
   * PEAK HOURS ANALYSIS
   * ============================================
   */

  /**
   * Get peak playing hours analysis
   * @param {string} studioId - Studio UUID
   * @param {string|null} gameId - Optional game UUID filter
   * @param {number} daysBack - Number of days to look back
   * @returns {Promise<Object>} Peak hours data with recommendations
   */
  async getPeakPlayingHours(studioId, gameId = null, daysBack = 30) {
    try {
      let query = supabase
        .from('user_game_sessions')
        .select(`
          session_start,
          game_id,
          games!inner(studio_id)
        `)
        .eq('games.studio_id', studioId)
        .gte('session_start', new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000).toISOString());

      if (gameId) {
        query = query.eq('game_id', gameId);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (!data || data.length === 0) {
        return {
          hourlyDistribution: Array(24).fill(0).map((_, hour) => ({ hour: `${hour}:00`, sessions: 0 })),
          dailyDistribution: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => ({ day, sessions: 0 })),
          insights: {
            peakHour: 'N/A',
            peakDay: 'N/A',
            recommendation: 'Not enough data yet'
          }
        };
      }

      // Analyze by hour and day
      const hourlyData = Array(24).fill(0);
      const dailyData = Array(7).fill(0);

      data.forEach(session => {
        const date = new Date(session.session_start);
        hourlyData[date.getUTCHours()]++;
        dailyData[date.getUTCDay()]++;
      });

      const peakHourIndex = hourlyData.indexOf(Math.max(...hourlyData));
      const peakDayIndex = dailyData.indexOf(Math.max(...dailyData));

      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayNamesShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

      return {
        hourlyDistribution: hourlyData.map((count, hour) => ({
          hour: `${hour.toString().padStart(2, '0')}:00`,
          sessions: count
        })),
        dailyDistribution: dailyData.map((count, day) => ({
          day: dayNamesShort[day],
          sessions: count
        })),
        insights: {
          peakHour: `${peakHourIndex.toString().padStart(2, '0')}:00 UTC`,
          peakDay: dayNames[peakDayIndex],
          recommendation: `Schedule updates or events around ${peakHourIndex.toString().padStart(2, '0')}:00 UTC on ${dayNames[peakDayIndex]}s for maximum engagement.`
        }
      };
    } catch (error) {
      console.error('Error fetching peak hours:', error);
      throw error;
    }
  },

  /**
   * ============================================
   * HELPER UTILITIES
   * ============================================
   */

  /**
   * Get color for heat map cell based on value
   * @param {number} value - Retention percentage
   * @returns {string} Tailwind color class
   */
  getHeatMapColor(value) {
    if (value >= 70) return 'bg-green-500';
    if (value >= 50) return 'bg-yellow-500';
    if (value >= 30) return 'bg-orange-500';
    return 'bg-red-500';
  },

  /**
   * Format percentage for display
   * @param {number} value - Percentage value
   * @returns {string} Formatted percentage
   */
  formatPercentage(value) {
    return `${parseFloat(value).toFixed(1)}%`;
  },

  /**
   * Get severity color class
   * @param {string} severity - Severity level
   * @returns {string} Tailwind color class
   */
  getSeverityColor(severity) {
    switch (severity) {
      case 'critical': return 'text-red-500 bg-red-500/10 border-red-500/20';
      case 'high': return 'text-orange-500 bg-orange-500/10 border-orange-500/20';
      case 'medium': return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20';
      case 'low': return 'text-blue-500 bg-blue-500/10 border-blue-500/20';
      default: return 'text-gray-500 bg-gray-500/10 border-gray-500/20';
    }
  },

  /**
   * ============================================
   * PEAK HOURS INTELLIGENCE
   * ============================================
   */

  /**
   * Get peak hours heat map data
   * @param {string} gameId - Game UUID
   * @param {number} daysBack - Number of days to analyze (default 30)
   * @returns {Promise<{heatMap: Object, peakHour: Object, patterns: Object, recommendations: Array}>}
   */
  async getPeakHoursData(gameId, daysBack = 30) {
    try {
      const { data, error } = await supabase.rpc('get_peak_hours_data', {
        p_game_id: gameId,
        p_days_back: daysBack
      });

      if (error) throw error;

      if (!data) {
        return {
          heatMap: {},
          peakHour: null,
          patterns: {
            busiestDay: null,
            quietestDay: null,
            weekendEffect: 0
          },
          recommendations: []
        };
      }

      return data;
    } catch (error) {
      console.error('Error fetching peak hours data:', error);
      throw error;
    }
  }
};
